/**
 * Finding Governance Actions Modal
 *
 * Provides UI for:
 * - Suppress (exclude from trust score)
 * - Risk Accept (formal approval workflow)
 * - Mute (silence notifications)
 * - Manual Resolution
 */
import React, { useState, useEffect } from 'react';
import { Modal, Tabs, Form, Input, DatePicker, Select, Button, Tag, Descriptions, Alert, Radio } from 'antd';
import {
  StopOutlined,
  SafetyCertificateOutlined,
  BellOutlined,
  CheckCircleOutlined,
  InfoCircleOutlined
} from '@ant-design/icons';
import {
  getFindingWithGovernance,
  suppressFinding,
  requestRiskAcceptance,
  muteFinding,
  resolveFindingManually
} from '../api/trustgridApi';
import { showError, showSuccess } from '../../../utils/notifications';
import moment from 'moment';

const { TextArea } = Input;
const { Option } = Select;
const { TabPane } = Tabs;

function FindingGovernanceModal({ visible, findingId, onClose, onSuccess }) {
  const [finding, setFinding] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState('info');

  const [suppressForm] = Form.useForm();
  const [riskAcceptForm] = Form.useForm();
  const [muteForm] = Form.useForm();
  const [resolveForm] = Form.useForm();

  useEffect(() => {
    if (visible && findingId) {
      fetchFinding();
    }
  }, [visible, findingId]);

  const fetchFinding = async () => {
    setLoading(true);
    try {
      const res = await getFindingWithGovernance(findingId);
      setFinding(res.data);
    } catch (error) {
      showError(error);
    } finally {
      setLoading(false);
    }
  };

  const handleSuppress = async (values) => {
    setSubmitting(true);
    try {
      await suppressFinding(findingId, {
        scope_type: values.scope_type,
        reason: values.reason,
        justification: values.justification,
        expires_at: values.expires_at ? values.expires_at.toISOString() : undefined
      });
      showSuccess('Finding suppressed successfully');
      suppressForm.resetFields();
      onSuccess();
      onClose();
    } catch (error) {
      showError(error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleRiskAccept = async (values) => {
    setSubmitting(true);
    try {
      await requestRiskAcceptance(findingId, {
        scope_type: values.scope_type,
        business_justification: values.business_justification,
        compensating_controls: values.compensating_controls ? values.compensating_controls.split('\n').filter(c => c.trim()) : [],
        approver_role: values.approver_role,
        expires_at: values.expires_at.toISOString()
      });
      showSuccess('Risk acceptance requested - pending approval');
      riskAcceptForm.resetFields();
      onSuccess();
      onClose();
    } catch (error) {
      showError(error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleMute = async (values) => {
    setSubmitting(true);
    try {
      await muteFinding(findingId, {
        reason: values.reason,
        muted_until: values.muted_until ? values.muted_until.toISOString() : undefined
      });
      showSuccess('Finding muted (still counts in score)');
      muteForm.resetFields();
      onSuccess();
      onClose();
    } catch (error) {
      showError(error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleResolve = async (values) => {
    setSubmitting(true);
    try {
      await resolveFindingManually(findingId, values.resolution_notes);
      showSuccess('Finding marked as resolved');
      resolveForm.resetFields();
      onSuccess();
      onClose();
    } catch (error) {
      showError(error);
    } finally {
      setSubmitting(false);
    }
  };

  const governance = finding?.governance || {};

  return (
    <Modal
      title="Governance Actions"
      visible={visible}
      onCancel={onClose}
      footer={null}
      width={800}
      destroyOnClose
    >
      {loading || !finding ? (
        <div style={{ textAlign: 'center', padding: 40 }}>Loading...</div>
      ) : (
        <>
          {/* Finding Summary */}
          <Descriptions bordered size="small" column={2} style={{ marginBottom: 16 }}>
            <Descriptions.Item label="Title" span={2}>{finding.finding?.title || 'N/A'}</Descriptions.Item>
            <Descriptions.Item label="Control">{finding.finding?.control_code || 'N/A'}</Descriptions.Item>
            <Descriptions.Item label="Severity">
              <Tag color={finding.finding?.severity === 'critical' ? 'red' : finding.finding?.severity === 'high' ? 'orange' : 'gold'}>
                {finding.finding?.severity?.toUpperCase() || 'N/A'}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Status">{finding.finding?.status || 'N/A'}</Descriptions.Item>
            <Descriptions.Item label="First Seen">
              {finding.finding?.first_seen_at ? new Date(finding.finding.first_seen_at).toLocaleString() : 'N/A'}
            </Descriptions.Item>
          </Descriptions>

          {/* Current Governance State */}
          {(governance.suppressed || governance.risk_accepted || governance.muted) && (
            <Alert
              message="Current Governance State"
              description={
                <div>
                  {governance.suppressed && (
                    <div><Tag color="purple"><StopOutlined /> Suppressed</Tag> {governance.suppression.reason}</div>
                  )}
                  {governance.risk_accepted && (
                    <div><Tag color="blue"><SafetyCertificateOutlined /> Risk Accepted</Tag> {governance.risk_acceptance.business_justification}</div>
                  )}
                  {governance.muted && (
                    <div><Tag color="orange"><BellOutlined /> Muted</Tag> {governance.mute.reason}</div>
                  )}
                </div>
              }
              type="info"
              style={{ marginBottom: 16 }}
            />
          )}

          {/* Governance Actions Tabs */}
          <Tabs activeKey={activeTab} onChange={setActiveTab}>
            <TabPane tab={<span><InfoCircleOutlined /> Info</span>} key="info">
              <div style={{ padding: 16 }}>
                <Descriptions bordered column={1} size="small">
                  <Descriptions.Item label="Description">
                    {finding.finding?.description || 'No description'}
                  </Descriptions.Item>
                  <Descriptions.Item label="Target">
                    {finding.finding?.target_type}: {finding.finding?.target_id}
                  </Descriptions.Item>
                  <Descriptions.Item label="Occurrence Count">
                    {finding.finding?.occurrence_count || 0}
                  </Descriptions.Item>
                  <Descriptions.Item label="Reopen Count">
                    {finding.finding?.reopen_count || 0}
                  </Descriptions.Item>
                </Descriptions>
              </div>
            </TabPane>

            <TabPane tab={<span><StopOutlined /> Suppress</span>} key="suppress" disabled={governance.suppressed}>
              <Form form={suppressForm} layout="vertical" onFinish={handleSuppress} style={{ marginTop: 16 }}>
                <Alert
                  message="Suppression excludes finding from Trust Score calculation"
                  type="warning"
                  showIcon
                  style={{ marginBottom: 16 }}
                />
                <Form.Item
                  name="scope_type"
                  label="Scope"
                  rules={[{ required: true }]}
                  initialValue="finding"
                >
                  <Radio.Group>
                    <Radio value="finding">This finding only</Radio>
                    <Radio value="control">All findings for this control</Radio>
                    <Radio value="target">All findings for this target</Radio>
                  </Radio.Group>
                </Form.Item>
                <Form.Item name="reason" label="Reason" rules={[{ required: true, message: 'Reason is required' }]}>
                  <TextArea rows={3} placeholder="Why are you suppressing this finding?" />
                </Form.Item>
                <Form.Item name="justification" label="Additional Context (optional)">
                  <TextArea rows={2} placeholder="Additional justification..." />
                </Form.Item>
                <Form.Item name="expires_at" label="Expires At (optional)">
                  <DatePicker showTime style={{ width: '100%' }} disabledDate={(d) => d && d < moment()} />
                </Form.Item>
                <Form.Item>
                  <Button type="primary" htmlType="submit" loading={submitting} icon={<StopOutlined />}>
                    Suppress Finding
                  </Button>
                </Form.Item>
              </Form>
            </TabPane>

            <TabPane tab={<span><SafetyCertificateOutlined /> Risk Accept</span>} key="risk_accept" disabled={governance.risk_accepted}>
              <Form form={riskAcceptForm} layout="vertical" onFinish={handleRiskAccept} style={{ marginTop: 16 }}>
                <Alert
                  message="Risk acceptance requires approval and excludes finding from Trust Score"
                  type="info"
                  showIcon
                  style={{ marginBottom: 16 }}
                />
                <Form.Item
                  name="scope_type"
                  label="Scope"
                  rules={[{ required: true }]}
                  initialValue="finding"
                >
                  <Radio.Group>
                    <Radio value="finding">This finding only</Radio>
                    <Radio value="control">All findings for this control</Radio>
                    <Radio value="target">All findings for this target</Radio>
                  </Radio.Group>
                </Form.Item>
                <Form.Item name="business_justification" label="Business Justification" rules={[{ required: true }]}>
                  <TextArea rows={4} placeholder="Explain the business reason for accepting this risk..." />
                </Form.Item>
                <Form.Item name="compensating_controls" label="Compensating Controls (one per line)">
                  <TextArea rows={3} placeholder="List any compensating controls..." />
                </Form.Item>
                <Form.Item name="approver_role" label="Approver Role" rules={[{ required: true }]}>
                  <Select placeholder="Select approver role">
                    <Option value="CISO">CISO</Option>
                    <Option value="ComplianceLead">Compliance Lead</Option>
                    <Option value="RiskManager">Risk Manager</Option>
                    <Option value="Director">Director</Option>
                  </Select>
                </Form.Item>
                <Form.Item name="expires_at" label="Expires At" rules={[{ required: true }]} extra="Max 1 year">
                  <DatePicker
                    showTime
                    style={{ width: '100%' }}
                    disabledDate={(d) => d && (d < moment() || d > moment().add(1, 'year'))}
                  />
                </Form.Item>
                <Form.Item>
                  <Button type="primary" htmlType="submit" loading={submitting} icon={<SafetyCertificateOutlined />}>
                    Request Risk Acceptance
                  </Button>
                </Form.Item>
              </Form>
            </TabPane>

            <TabPane tab={<span><BellOutlined /> Mute</span>} key="mute" disabled={governance.muted}>
              <Form form={muteForm} layout="vertical" onFinish={handleMute} style={{ marginTop: 16 }}>
                <Alert
                  message="Muting silences notifications but finding still counts in Trust Score"
                  type="info"
                  showIcon
                  style={{ marginBottom: 16 }}
                />
                <Form.Item name="reason" label="Reason" rules={[{ required: true }]}>
                  <TextArea rows={3} placeholder="Why are you muting this finding?" />
                </Form.Item>
                <Form.Item name="muted_until" label="Mute Until (optional)" extra="Default: 7 days">
                  <DatePicker showTime style={{ width: '100%' }} disabledDate={(d) => d && d < moment()} />
                </Form.Item>
                <Form.Item>
                  <Button type="primary" htmlType="submit" loading={submitting} icon={<BellOutlined />}>
                    Mute Finding
                  </Button>
                </Form.Item>
              </Form>
            </TabPane>

            <TabPane tab={<span><CheckCircleOutlined /> Resolve</span>} key="resolve">
              <Form form={resolveForm} layout="vertical" onFinish={handleResolve} style={{ marginTop: 16 }}>
                <Alert
                  message="Manually mark finding as resolved"
                  type="success"
                  showIcon
                  style={{ marginBottom: 16 }}
                />
                <Form.Item name="resolution_notes" label="Resolution Notes" rules={[{ required: true }]}>
                  <TextArea rows={5} placeholder="Describe how this issue was resolved..." />
                </Form.Item>
                <Form.Item>
                  <Button type="primary" htmlType="submit" loading={submitting} icon={<CheckCircleOutlined />}>
                    Mark as Resolved
                  </Button>
                </Form.Item>
              </Form>
            </TabPane>
          </Tabs>
        </>
      )}
    </Modal>
  );
}

export default FindingGovernanceModal;
